type PaginationMeta = {
  total?: number;
  totalCount?: number;
  pages?: number;
  totalPages?: number;
  page?: number;
  currentPage?: number;
  limit?: number;
  perPage?: number;
};

type NestedListPayload = PaginationMeta & {
  data?: unknown[];
  transactions?: unknown[];
  settlements?: unknown[];
  list?: unknown[];
  pagination?: PaginationMeta;
};

export type WalletListPageResult<T> = {
  items: T[];
  total: number;
  totalPages: number;
  page: number;
  limit: number;
};

type RawListResponse = {
  data?: unknown[] | NestedListPayload;
  total?: number;
  totalCount?: number;
  pages?: number;
  totalPages?: number;
  page?: number;
  limit?: number;
  pagination?: PaginationMeta;
};

function pickItems(
  nested: unknown[] | NestedListPayload | undefined,
  keys: Array<'transactions' | 'settlements' | 'data' | 'list'>,
): unknown[] {
  if (Array.isArray(nested)) return nested;
  if (!nested || typeof nested !== 'object') return [];
  for (const key of keys) {
    const value = nested[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

/** Normalize wallet list API shapes (transactions / settlements) into stable pagination. */
export function normalizeWalletListResponse<T>(
  raw: RawListResponse | undefined,
  fallback: { page: number; limit: number },
  itemKeys: Array<'transactions' | 'settlements' | 'data' | 'list'>,
): WalletListPageResult<T> {
  const nested = raw?.data;
  const items = pickItems(nested, itemKeys) as T[];

  const pagination =
    (!Array.isArray(nested) ? nested?.pagination : undefined) ?? raw?.pagination;

  const total = Number(
    (!Array.isArray(nested) ? nested?.total ?? nested?.totalCount : undefined) ??
      raw?.total ??
      raw?.totalCount ??
      pagination?.total ??
      pagination?.totalCount ??
      items.length,
  );

  const limit =
    Number(
      (!Array.isArray(nested)
        ? nested?.limit ?? nested?.perPage
        : undefined) ??
        raw?.limit ??
        pagination?.limit ??
        pagination?.perPage ??
        fallback.limit,
    ) || fallback.limit;

  const page =
    Number(
      (!Array.isArray(nested) ? nested?.page : undefined) ??
        raw?.page ??
        pagination?.page ??
        pagination?.currentPage ??
        fallback.page,
    ) || fallback.page;

  const computedPages =
    limit > 0 && Number.isFinite(total) ? Math.ceil(total / limit) : 1;

  // If API omits totalPages but returned a full page, allow at least Next.
  const heuristicPages =
    items.length >= limit ? Math.max(page + 1, computedPages) : computedPages;

  const totalPages = Math.max(
    1,
    Number(
      (!Array.isArray(nested)
        ? nested?.pages ?? nested?.totalPages
        : undefined) ??
        raw?.pages ??
        raw?.totalPages ??
        pagination?.pages ??
        pagination?.totalPages ??
        heuristicPages,
    ) || 1,
  );

  return {
    items,
    total: Number.isFinite(total) ? total : items.length,
    totalPages,
    page,
    limit,
  };
}
